"use client";

import { useContext, useEffect, useMemo, useState } from "react";

import { Docs } from "~/bff/types/Docs";
import { clickAndCollectProductStatusGA } from "~/components/product-availability-block/helpers/click-and-collect-product-status-ga";
import { clickAndCollectStoreStatusGA } from "~/components/product-availability-block/helpers/click-and-collect-store-status-ga";
import {
  DataLayerData,
  EventTypes,
  EventsNames,
  NewDataLayerDataTypes,
  NewProductDataLayerData,
} from "~/constants/data-layer";
import { getCurrencyCodeByLocale, LOCALES } from "~/constants/i18n";
import { DictionaryLabelContext } from "~/context/dictionary-label/context";
import { DictionaryKeys } from "~/context/dictionary-label/types";
import { EMPTY_ROUTE_HISTORY_MASK } from "~/context/router-history/constants";
import { RouterHistoryContext } from "~/context/router-history/context";
import { getCountryCode } from "~/helpers/country/get-country-code";
import {
  getDisplayPriceFromVariants,
  getDisplayPriceWithoutCurrency,
} from "~/helpers/get-display-price";
import { getPageCategoriesTitles } from "~/helpers/get-page-categories-titles";
import { pushDataLayer } from "~/helpers/push-data-layer";
import { useStoreSelectorData } from "~/hooks/store-selector/use-store-selector-data";
import { Nullable } from "~/types/general.types";
import { parseCategoryPathsDisplayableToGa } from "~/utils/data/bloomreach/parse-field/category-paths-displayable";
import { Logger } from "~/utils/logger";

import { clickAndCollectOverallStatusGA } from "../../product-availability-block/helpers/click-and-collect-overall-status-ga";

type UseDataForGoogleAnalytics = (args: {
  locale: LOCALES;
  queryParam: string;
  totalProductsCount: Nullable<number>;
  products: Nullable<Nullable<Docs>[]>;
  isUserAuthenticated: boolean;
  sortOrderForGA: string | undefined;
  pageSize: number;
}) => void;

const getPathname = (url: string | undefined) => {
  if (!url) {
    return undefined;
  }
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.pathname;
  } catch {
    Logger.getLogger().error(`Invalid URL provided: "${url}"`);
  }
};

export const useDataForGoogleAnalytics: UseDataForGoogleAnalytics = (args) => {
  const {
    locale,
    queryParam,
    totalProductsCount,
    products,
    isUserAuthenticated,
    sortOrderForGA,
    pageSize,
  } = args;

  const dictionary = useContext(DictionaryLabelContext);
  const previousPath = useContext(RouterHistoryContext)?.previousPath;
  const validPreviousPath =
    previousPath === EMPTY_ROUTE_HISTORY_MASK ? "" : previousPath;
  const previousPathname = getPathname(validPreviousPath);
  const { pageCategory, pageSubCategory, pageSubSubCategory } =
    getPageCategoriesTitles(null, previousPathname);

  const fromLabel = dictionary ? dictionary[DictionaryKeys.FromLabel] : "";
  const { currentStore, isLoading } = useStoreSelectorData(null);
  const isLoadMore = (products || []).length > pageSize;

  const [prevProductsPids, setPrevProductsPids] = useState<Docs["pid"][]>([]);

  const data: NewDataLayerDataTypes = useMemo(() => {
    const country = getCountryCode(locale);
    const currency = getCurrencyCodeByLocale(locale);

    const gaProducts = products?.filter(
      (product) => !prevProductsPids.includes(product?.pid),
    );

    const items = gaProducts?.map((product, index) => {
      const itemPrice = getDisplayPriceWithoutCurrency(
        getDisplayPriceFromVariants({
          variants: product?.variants,
          masterVariant: {
            price: product?.price?.toString(),
          },
          locale,
          currencyCode: currency,
          fromLabel,
        }),
      );
      const isProductClickAndCollect =
        product?.clickAndCollectExclusive || product?.clickAndCollect;

      return {
        [NewProductDataLayerData.itemId]: product?.pid,
        [NewProductDataLayerData.itemName]: product?.title,
        [NewProductDataLayerData.index]: index + 1,
        [NewProductDataLayerData.itemBrand]: product?.brand,
        ...parseCategoryPathsDisplayableToGa(product?.categoryPathsDisplayable),
        [NewProductDataLayerData.itemCategory5]: clickAndCollectOverallStatusGA(
          product,
          currentStore,
        ),
        [NewProductDataLayerData.dimension49]:
          clickAndCollectStoreStatusGA(currentStore),
        [NewProductDataLayerData.dimension50]: isProductClickAndCollect,
        [NewProductDataLayerData.dimension51]: clickAndCollectOverallStatusGA(
          product,
          currentStore,
        ),
        [NewProductDataLayerData.dimension53]:
          clickAndCollectProductStatusGA(product),
        [NewProductDataLayerData.price]: Number(itemPrice),
        [NewProductDataLayerData.quantity]: 1,
      };
    });

    return {
      [DataLayerData.event]: EventsNames.viewItemList,
      [DataLayerData.siteCountry]: country,
      [DataLayerData.type]: isLoadMore ? EventTypes.loadMore : EventTypes.search,
      [DataLayerData.pageCategory]: "Search",
      [DataLayerData.pageSubCategory]: undefined,
      [DataLayerData.pageSubSubCategory]: undefined,
      [DataLayerData.prevPageUrl]: isLoadMore ? undefined : validPreviousPath,
      [DataLayerData.prevPageCategory]: isLoadMore ? undefined : pageCategory,
      [DataLayerData.prevPageSubCategory]: isLoadMore ? undefined : pageSubCategory,
      [DataLayerData.prevPageSubSubCategory]: isLoadMore
        ? undefined
        : pageSubSubCategory,
      [DataLayerData.loggedInStatus]: isUserAuthenticated,
      [DataLayerData.searchTermUsed]: queryParam,
      [DataLayerData.searchResults]: totalProductsCount,
      [DataLayerData.storeSelected]: currentStore?.geomodifier,
      [DataLayerData.sortOrder]: sortOrderForGA,
      [DataLayerData.ecommerce]: {
        [DataLayerData.currency]: currency,
        [DataLayerData.itemListName]: `Search: ${queryParam}`,
        [DataLayerData.items]: items,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStore,
    fromLabel,
    isUserAuthenticated,
    locale,
    products,
    validPreviousPath,
  ]);

  useEffect(() => {
    if (!isLoading) {
      pushDataLayer(data);

      const currProductsPids = products?.map((el) => el?.pid);
      setPrevProductsPids(currProductsPids ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading]);
};
